import React, { useCallback, useEffect, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';


export const AccountActionContactsMaxSection = ({ value, onUpdate, showError }: {
    value: number;
    onUpdate?: (value: number) => void;
    showError?: boolean;
}) => {
    /**
     * keep a local copy of the input content to allow temporary invalid values such as an empty string
     * note in this way, the actual value stored in the trigger schema can be kept as a positive integer
     * while still providing convincing user interactions such as error handling
     */
    const [maxContacts, setMaxContacts] = useState('');

    /**
     * sync the local input value with the one from the trigger schema, if they are different for some reason
     */
    useEffect(() => {
        if (value === 0) {
            setMaxContacts('');
        } else if (value.toString() !== maxContacts) {
            setMaxContacts(value.toString());
        }
    }, [value, maxContacts, setMaxContacts]);

    /**
     * determine if the error message should display
     */
    const isError = useMemo(() => showError && !/^[1-9]\d*$/.test(maxContacts), [maxContacts, showError]);

    const handleUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onUpdate?.(Number(e.target.value ?? 0)); // update the trigger schema
        setMaxContacts(e.target.value); // update the local value
    }, [onUpdate, setMaxContacts]);

    return (
        <div className={`workflow-node-panel-account-action-max-contacts${isError ? ' has-error' : ''}`}>
            <label className='control-label'>{i18n.gettext('Maximum contacts per account')}</label>
            <input
                type='number'
                min={1}
                placeholder={i18n.gettext('Type in a number')}
                value={maxContacts}
                onChange={handleUpdate}
                data-testid='workflow-account-panel-max-contacts-input'
                className='form-control'
            />
            {isError && (
                <p className='workflow-node-panel-select-dropdown-error-msg'>
                    {i18n.gettext('Must be a positive whole number.')}
                </p>
            )}
        </div>
    );
};
